import React, { useEffect, useMemo, useState } from "react";
import Modal from "@amzn/awsui-components-console/modal";
import Box from "@amzn/awsui-components-console/box";
import Button from "@amzn/awsui-components-console/button";
import SpaceBetween from "@amzn/awsui-components-console/space-between";
import FormField from "@amzn/awsui-components-console/form-field";
import Select from "@amzn/awsui-components-console/select";
import Table from "@amzn/awsui-components-console/table";
import Grid from "@amzn/awsui-components-console/grid";
import Toggle from "@amzn/awsui-components-console/toggle";
import TextFilter from "@amzn/awsui-components-console/text-filter";
import { getAllFixedFeeConfigColumns } from "src/components/allFixedFeeConfigsPage/fixed-fee-config-attributes";
import {
  ColumnProfileId,
  CustomProfileId,
  useColumnProfilesStore,
} from "src/stores/useColumnProfilesStore";

const PROFILE_LABELS: Record<ColumnProfileId, string> = {
  default: "Default Profile",
  custom1: "Custom Profile 1",
  custom2: "Custom Profile 2",
};

const PROFILE_IDS = Object.keys(PROFILE_LABELS) as ColumnProfileId[];

interface ColumnItem {
  id: string;
  label: string;
}

interface CustomizeModalProps {
  visible: boolean;
  onDismiss: () => void;
}

export const CustomizeModal: React.FC<CustomizeModalProps> = ({
  visible,
  onDismiss,
}) => {
  const {
    activeProfile,
    customProfiles,
    customConcise,
    setActiveProfile,
    saveCustomProfile,
  } = useColumnProfilesStore();

  const allColumns = useMemo<ColumnItem[]>(
    () => getAllFixedFeeConfigColumns(),
    [],
  );
  const allColumnIds = useMemo(() => allColumns.map((c) => c.id), [allColumns]);

  const [selectedProfile, setSelectedProfile] =
    useState<ColumnProfileId>(activeProfile);
  const [chosenIds, setChosenIds] = useState<string[]>(allColumnIds);
  // UI-only for now; not yet wired to how the page renders.
  const [conciseView, setConciseView] = useState<boolean>(false);
  // Filters which columns are shown in the selector; does not affect the
  // stored selection of columns filtered out of view.
  const [columnSearch, setColumnSearch] = useState<string>("");

  const isDefault = selectedProfile === "default";

  // Resolve a profile's stored selection into an ordered list of column ids.
  // Default (and never-customized custom profiles) show all columns.
  const resolveProfileColumns = (profile: ColumnProfileId): string[] => {
    if (profile === "default") return allColumnIds;
    const stored = customProfiles[profile];
    return stored
      ? allColumnIds.filter((id) => stored.includes(id))
      : allColumnIds;
  };

  // Default profile is never concise; custom profiles use their stored value.
  const resolveProfileConcise = (profile: ColumnProfileId): boolean =>
    profile === "default" ? false : customConcise[profile];

  // Sync local editing state from the store each time the modal opens.
  useEffect(() => {
    if (visible) {
      setSelectedProfile(activeProfile);
      setChosenIds(resolveProfileColumns(activeProfile));
      setConciseView(resolveProfileConcise(activeProfile));
      setColumnSearch("");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [visible]);

  const onProfileChange = (profile: ColumnProfileId) => {
    setSelectedProfile(profile);
    setChosenIds(resolveProfileColumns(profile));
    setConciseView(resolveProfileConcise(profile));
    setColumnSearch("");
  };

  const chosenSet = useMemo(() => new Set(chosenIds), [chosenIds]);

  // Columns shown in the selector after applying the search filter.
  const visibleColumns = useMemo(() => {
    const query = columnSearch.trim().toLowerCase();
    if (!query) return allColumns;
    return allColumns.filter((c) => c.label.toLowerCase().includes(query));
  }, [allColumns, columnSearch]);

  // Only the visible-and-chosen columns are passed to the table as selected.
  const selectedItems = useMemo(
    () => visibleColumns.filter((c) => chosenSet.has(c.id)),
    [visibleColumns, chosenSet],
  );

  // Update the selection for visible columns while preserving the chosen
  // state of any columns currently filtered out by the search.
  const onSelectionChange = (selected: ColumnItem[]) => {
    const visibleIds = new Set(visibleColumns.map((c) => c.id));
    const preserved = chosenIds.filter((id) => !visibleIds.has(id));
    setChosenIds([...preserved, ...selected.map((i) => i.id)]);
  };

  // Compare the current selection against the profile's persisted value so
  // Save is only enabled when something actually changed.
  const hasChanges = useMemo(() => {
    if (resolveProfileConcise(selectedProfile) !== conciseView) return true;
    const stored = resolveProfileColumns(selectedProfile);
    if (stored.length !== chosenIds.length) return true;
    return stored.some((id) => !chosenSet.has(id));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    selectedProfile,
    customProfiles,
    customConcise,
    conciseView,
    chosenIds,
    chosenSet,
    allColumnIds,
  ]);

  const isActive = selectedProfile === activeProfile;

  // Mark the active profile in the dropdown so it's clear which one is applied.
  const profileLabel = (id: ColumnProfileId): string =>
    id === activeProfile
      ? `${PROFILE_LABELS[id]} (Currently Active Profile)`
      : PROFILE_LABELS[id];
  const profileOptions = PROFILE_IDS.map((id) => ({
    value: id,
    label: profileLabel(id),
  }));

  // Save persists the profile's edits and activates it. "Set as active
  // profile" activates without any edits.
  const handleSave = () => {
    if (!isDefault) {
      saveCustomProfile(
        selectedProfile as CustomProfileId,
        chosenIds,
        conciseView,
      );
    }
    setActiveProfile(selectedProfile);
    onDismiss();
  };

  const handleSetActive = () => {
    setActiveProfile(selectedProfile);
    onDismiss();
  };

  return (
    <Modal
      visible={visible}
      onDismiss={onDismiss}
      header="Customization"
      closeAriaLabel="Close customization"
      size="medium"
      footer={
        <Box float="right">
          <SpaceBetween direction="horizontal" size="xs">
            <Button variant="link" onClick={onDismiss}>
              Cancel
            </Button>
            <Button onClick={handleSetActive} disabled={isActive || hasChanges}>
              Set as active profile
            </Button>
            <Button
              variant="primary"
              onClick={handleSave}
              disabled={!hasChanges}
            >
              Save
            </Button>
          </SpaceBetween>
        </Box>
      }
    >
      <SpaceBetween size="l">
        <FormField label="Settings profile">
          <Select
            selectedOption={{
              value: selectedProfile,
              label: profileLabel(selectedProfile),
            }}
            options={profileOptions}
            onChange={({ detail }) =>
              onProfileChange(detail.selectedOption.value as ColumnProfileId)
            }
          />
        </FormField>
        <Grid gridDefinition={[{ colspan: 8 }, { colspan: 4 }]}>
          <FormField
            label={`Columns (${chosenSet.size}/${allColumns.length})`}
            description={
              isDefault
                ? "The Default profile shows all columns and can't be edited."
                : "Select the columns to display for this profile."
            }
          >
            <SpaceBetween size="xs">
              <TextFilter
                filteringText={columnSearch}
                filteringPlaceholder="Find columns"
                filteringAriaLabel="Find columns"
                onChange={({ detail }) => setColumnSearch(detail.filteringText)}
              />
              <div style={{ maxHeight: 280, overflowY: "auto" }}>
                <Table
                  variant="embedded"
                  items={visibleColumns}
                  trackBy="id"
                  selectionType="multi"
                  selectedItems={selectedItems}
                  isItemDisabled={() => isDefault}
                  onSelectionChange={({ detail }) =>
                    onSelectionChange(detail.selectedItems)
                  }
                  empty="No columns match your search."
                  columnDefinitions={[
                    {
                      id: "label",
                      header: "Column",
                      cell: (item: ColumnItem) => item.label,
                    },
                  ]}
                  ariaLabels={{
                    selectionGroupLabel: "Columns",
                    allItemsSelectionLabel: () => "Select all columns",
                    itemSelectionLabel: (_sel, item: ColumnItem) => item.label,
                  }}
                />
              </div>
            </SpaceBetween>
          </FormField>
          <Toggle
            checked={conciseView}
            disabled={isDefault}
            onChange={({ detail }) => setConciseView(detail.checked)}
            description="Limit list type columns to display first 3 items only"
          >
            Concise View
          </Toggle>
        </Grid>
      </SpaceBetween>
    </Modal>
  );
};
